import { isUserAuthenticated } from "@/middleware/auth";
import {
  removeChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";

import { SocketEventActionType, ChatEventType } from "./types";

import { useNavigationState } from "@/zustand/NavigationState";
import { removeChatDataFromLocalDB } from "@/indexedDB/services/chatTeardown";
import { navigateToChatList } from "@/lib/navigationRef";
import { queryClient } from "@/queryClient";
import { MemberRole } from "@/server/types/member";

export const handleChatEvent = async (data: ChatEventType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  switch (data.action) {
    case SocketEventActionType.UPDATE:
      await onUpdateChat({ eventPayload: data });
      break;
    case SocketEventActionType.DELETE:
      await onDeleteChat({ eventPayload: data });
      break;

    default:
      break;
  }
};

// On delete chat
const onDeleteChat = async ({
  eventPayload,
}: {
  eventPayload: ChatEventType;
}) => {
  const activeChatId = useNavigationState.getState().activeChatId;
  // if chat id is active, navigate away via URL (URL-first pattern)
  if (eventPayload.chat.id === activeChatId) {
    navigateToChatList();
  }

  // Sweep EVERYTHING the chat left behind (messages, pinned, attachment
  // rows + their decrypted OPFS blobs, links, per-chat query caches) — chat
  // teardown is a hot path for account deletion (note-to-self, solo
  // Architect chats, last-participant chats all arrive as chat Delete).
  await removeChatDataFromLocalDB(eventPayload.chat.id);
  // remove chat from local db
  await removeChatFromLocalDB(eventPayload.chat.id);
  // invalidate the query for the current user's public chats
  queryClient.invalidateQueries({
    queryKey: ["admin-user-public-chat"],
  });
};

// On update chat
const onUpdateChat = async ({
  eventPayload,
}: {
  eventPayload: ChatEventType;
}) => {
  const activeChatId = useNavigationState.getState().activeChatId;
  const setActiveChatName = useNavigationState.getState().setActiveChatName;
  const setActiveChatIsPublic =
    useNavigationState.getState().setActiveChatIsPublic;
  const setActiveChatDescription =
    useNavigationState.getState().setActiveChatDescription;
  const setActiveChatAllowThreads =
    useNavigationState.getState().setActiveChatAllowThreads;
  const setActiveChatCanPost =
    useNavigationState.getState().setActiveChatCanPost;
  const setActiveChatAvatar = useNavigationState.getState().setActiveChatAvatar;

  // if chat id is active
  if (eventPayload.chat.id === activeChatId) {
    setActiveChatName(eventPayload.chat.name);
    setActiveChatDescription(eventPayload.chat.description);
    setActiveChatAllowThreads(!!eventPayload.chat.threads_enabled);
    setActiveChatCanPost(
      eventPayload.chat.can_post === null
        ? MemberRole.MEMBER
        : eventPayload.chat.can_post,
    );
    setActiveChatIsPublic(eventPayload.chat.is_searchable);
    setActiveChatAvatar({
      attachmentId: eventPayload.chat.picture,
    });
  }

  // update on local db
  await updateChatInLocalDB(eventPayload.chat.id, {
    name: eventPayload.chat.name,
    description: eventPayload.chat.description,
    isPublic: eventPayload.chat.is_searchable,
    allowThreads: !!eventPayload.chat.threads_enabled,
    canPost:
      eventPayload.chat.can_post === null
        ? MemberRole.MEMBER
        : eventPayload.chat.can_post,
    avatar: {
      attachmentId: eventPayload.chat.picture,
    },
  });

  // invalidate the query for the current user's public chats
  queryClient.invalidateQueries({
    queryKey: ["admin-user-public-chat"],
  });
};
